/**
 * Phone Numbers API Client
 *
 * User phone number management for SMS verification.
 * Uses vb-server v1 API endpoints.
 */

import { serverGet, serverPost, serverPut, serverDelete } from './client';

// =============================================================================
// TYPES
// =============================================================================

export interface PhoneNumber {
  id: string;
  number: string;
  channel_id: string | null;
  status: 'Verified' | 'Not verified';
  created: number | null;
  verified: number | null;
}

export interface CreatePhoneInput {
  phone: string;
  channel_id?: string;
}

export interface CreatePhoneResponse {
  phone: string;
  status: 'pending';
}

export interface VerifyPhoneResponse {
  phone: string;
  status: 'Verified';
  verified: number;
}

// =============================================================================
// API FUNCTIONS
// =============================================================================

/**
 * Get current user's phone numbers
 *
 * @returns Array of phone numbers
 */
export async function listPhones(): Promise<PhoneNumber[]> {
  const response = await serverGet<PhoneNumber[]>('/users/me/phones');
  return response || [];
}

/**
 * Add a new phone number (starts verification process)
 * Server will send SMS verification code
 *
 * @param input - Phone number and optional channel
 * @returns Created phone info
 */
export async function createPhone(input: CreatePhoneInput): Promise<CreatePhoneResponse> {
  return serverPost<CreatePhoneResponse>('/users/me/phones', input);
}

/**
 * Verify a phone number with the SMS code
 *
 * @param phone - Phone number in E.164 format (e.g. +12125551234)
 * @param code - Verification code from SMS
 * @returns Verified phone info
 */
export async function verifyPhone(phone: string, code: string): Promise<VerifyPhoneResponse> {
  return serverPut<VerifyPhoneResponse>(`/users/me/phones/${encodeURIComponent(phone)}`, { code });
}

/**
 * Delete a phone number
 *
 * @param phone - Phone number to remove
 */
export async function deletePhone(phone: string): Promise<void> {
  await serverDelete(`/users/me/phones/${encodeURIComponent(phone)}`);
}
